import SimpleDialog, { SimpleDialogProps } from '../../../components/dialogs/SimpleDialog/SimpleDialog';
import styled from 'styled-components';
import { InputText, Select, SelectRadioButton } from '../../ui';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { AppState } from '../../../store/state';
import { selectKeywordsLevels, selectKeywordState, selectTrackUsingKeywords } from '../../../store/selectors';
import { AppDispatch } from '../../../store/store';
import keywordSlice from '../../../store/keyword/slice';
import { OpenSnackBarDTO } from '../../../store/error/types';
import errorSlice from '../../../store/error/slice';
import { connect } from 'react-redux';
import FileUploadUpdater from '../../FileUploadUpdater/FileUploadUpdater';
import { postKeywordsBulk } from '../../../store/keyword/sagas';
import tw from 'twin.macro';

type KEYWORD_INPUT_TYPE = 'single' | 'many';
type KEYWORD_RELATIONSHIP = 'parent' | 'child';

interface Props extends SimpleDialogProps {
  keywordState: KeywordState;
  createKeywordAction: (data: KeywordDTO) => void;
  getKeywordsAction: () => void;
  openSnackBarAction: (data: OpenSnackBarDTO) => void;
  trackUsingKeywords: boolean | null;
  keywordsLevel: KeywordsLevels;
}

const AddKeywordsDialog: React.FC<Props> = ({
  keywordState,
  createKeywordAction,
  getKeywordsAction,
  openSnackBarAction,
  trackUsingKeywords,
  keywordsLevel,
  ...rest
}) => {
  const { t } = useTranslation();

  const initKeyword = { id: 0, name: '', parent_id: null };
  const currentKLevels = keywordsLevel;

  const [keywordData, setKeywordData] = useState<KeywordDTO>(initKeyword);
  const [numLevels, setNumLevels] = useState<KeywordsLevels>(currentKLevels);
  const [mode, setMode] = useState<KEYWORD_INPUT_TYPE | undefined>(undefined);
  const [file, setFile] = useState<File | undefined>(undefined);
  const [showSave, setShowSave] = useState<boolean>(false);
  const [relationship, setRelationship] = useState<KEYWORD_RELATIONSHIP | undefined>('parent');

  const getParentIds = (): number[] => {
    return Object.keys(keywordTree).map((keywordId) => parseInt(keywordId));
  };

  const onRelationshipChange = (value: KEYWORD_RELATIONSHIP | undefined) => {
    setRelationship(value);
    setKeywordData(initKeyword);
    setFile(undefined);
  };

  const onModeChange = (value: KEYWORD_INPUT_TYPE | undefined) => {
    setMode(value);
    onRelationshipChange('parent');
  };

  const onNumLevelsChange = (value: KeywordsLevels) => {
    setNumLevels(value);
    onModeChange(undefined);
  };

  const { keywordById, keywordTree } = keywordState;
  const hasKeywords = Object.keys(keywordTree).length > 0;

  // If mode is single and name is empty or keyword is child and there's no selected parent, save button is disabled
  const saveIsDisabled =
    !numLevels ||
    !mode ||
    !relationship ||
    (mode == 'single' && (!keywordData.name || (relationship == 'child' && !keywordData.parent_id))) ||
    (mode == 'many' && !showSave);

  const kLevels = !currentKLevels ? numLevels : undefined;

  return (
    <SimpleDialog
      {...rest}
      disableOkButton={saveIsDisabled}
      handleOk={async () => {
        if (mode == 'single') {
          createKeywordAction({
            ...keywordData,
            keywords_levels: kLevels,
          });
        } else if (file) {
          const result = await postKeywordsBulk({ file, dry_run: false, keywords_levels: kLevels });
          switch (result.type) {
            case 'ok':
              getKeywordsAction(); // Refresh keywords
              openSnackBarAction({ message: t('Data saved with success.'), severity: 'success' });
              break;
            case 'validation-error':
              openSnackBarAction({ message: result.value.validation[0].message, severity: 'error' });
              break;
          }
        }
      }}
    >
      <StyledTitle>{t('Add Keywords')}</StyledTitle>
      {!currentKLevels && (
        <StyledFieldsetWrapper>
          <fieldset className={`text-sm`}>
            <StyledColSelectRadioButton
              label={t('Choose your keyword structure:')}
              options={[
                { id: '1', label: t('1 level: A simple list of keywords.') },
                { id: '2', label: t('2 levels: Keywords grouped into categories (nested).') },
              ]}
              value={numLevels?.toString()}
              onChange={(value) => onNumLevelsChange(parseInt(value) as KeywordsLevels)}
            />
            <p style={{ fontStyle: 'italic', marginTop: '1rem' }}>
              This choice is locked in once saved, so pick the structure that best suits your needs.
            </p>
          </fieldset>
        </StyledFieldsetWrapper>
      )}
      {numLevels > 0 && (
        <>
          <StyledFieldsetWrapper>
            <fieldset className={`text-sm`}>
              <StyledSelectRadioButton
                label={t('How many keywords do you want to create?')}
                options={[
                  { id: 'single', label: t('Just one') },
                  { id: 'many', label: t('Many') },
                ]}
                value={mode}
                onChange={(value) => onModeChange(value as KEYWORD_INPUT_TYPE)}
              />
            </fieldset>
          </StyledFieldsetWrapper>
          {mode && (
            <StyledContent>
              {mode == 'single' ? (
                <fieldset className="fieldsetWrapper fieldsetSingle">
                  <legend className="fieldsetLegend">{t('New keyword')}</legend>

                  {/* Select if we insert a parent keyword or child keyword */}
                  {numLevels > 1 && hasKeywords && (
                    <div className="mb-3">
                      <StyledSelectRadioButton
                        label={t('Keyword type') + ':'}
                        options={[
                          { id: 'parent', label: t('Area') },
                          { id: 'child', label: t('Topic') },
                        ]}
                        value={relationship}
                        onChange={(value) => onRelationshipChange(value as KEYWORD_RELATIONSHIP)}
                      />
                    </div>
                  )}

                  <StyledInputTextWrapper className="flex flex-row mb-3">
                    <InputText
                      label={t('Name')}
                      value={keywordData.name}
                      onChange={(e) => setKeywordData({ ...keywordData, name: e.target.value })}
                      className="w-full"
                    />
                  </StyledInputTextWrapper>

                  {relationship === 'child' && (
                    <StyledSelectParentKeywords className="mt-4 flex items-center">
                      <Select
                        label={t('Area')}
                        options={getParentIds().map((keywordId) => ({
                          id: keywordId.toString(),
                          label: keywordById[keywordId].name,
                        }))}
                        defaultItem={{ label: t('Choose area') }}
                        onChange={(value) => setKeywordData({ ...keywordData, parent_id: parseInt(value) })}
                        value={keywordData.parent_id?.toString()}
                      />
                    </StyledSelectParentKeywords>
                  )}
                </fieldset>
              ) : (
                <fieldset className="fieldsetWrapper fieldsetMany">
                  <legend className="fieldsetLegend">{t('Keywords bulk upload')}</legend>

                  <div className="my-2">
                    <FileUploadUpdater
                      templateName="keywords"
                      templateParams={{ keywords_levels: numLevels }}
                      doPreview={(file) => {
                        setFile(file);
                        return postKeywordsBulk({ file, dry_run: true, keywords_levels: kLevels });
                      }}
                      onFeedbackHandler={(data) => {
                        const hasNoError = data.data.rows.filter((value) => !value.has_error).length > 0;
                        setShowSave(hasNoError);
                      }}
                    />
                  </div>
                </fieldset>
              )}
            </StyledContent>
          )}
        </>
      )}
    </SimpleDialog>
  );
};

const mapStateToProps = (state: AppState) => ({
  keywordState: selectKeywordState(state),
  trackUsingKeywords: selectTrackUsingKeywords(state),
  keywordsLevel: selectKeywordsLevels(state),
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  createKeywordAction: (data: KeywordDTO) => dispatch(keywordSlice.actions.CREATE_KEYWORD(data)),
  getKeywordsAction: () => dispatch(keywordSlice.actions['GET_KEYWORDS']()),
  openSnackBarAction: (data: OpenSnackBarDTO) => dispatch(errorSlice.actions['OPEN:SNACKBAR'](data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddKeywordsDialog);

const StyledTitle = styled.h2`
  border-bottom: 2px dotted #e3e5e6;
  padding: 0;
  margin-bottom: 2rem;
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.6;
  letter-spacing: 0.0075em;
`;

const StyledFieldsetWrapper = styled.div`
  margin-bottom: 1.25rem;
  fieldset {
    border: 1px solid #ccc;
    border-radius: 4px;
    padding: 1rem;
  }
  .disabled {
    > div {
      label {
        color: #ccc;
        pointer-events: none;
      }
      > div {
        input {
          border-color: #ccc;
          pointer-events: none;
        }
      }
    }
  }
`;

const StyledSelectRadioButton = styled(SelectRadioButton)`
  display: flex;
  align-items: center;

  label {
    margin-right: 1rem;
    vertical-align: text-bottom;
  }
`;

const StyledColSelectRadioButton = styled(SelectRadioButton)`
  display: flex;
  flex-direction: column;
  ${tw`gap-y-1`}

  label {
    margin-right: 1rem;
    vertical-align: text-bottom;
  }
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 1.25rem;

  fieldset {
    width: 100%;
  }

  .fieldsetWrapper {
    padding: 1rem;
    border: 1px solid #ccc;
    border-radius: 5px;
  }

  .fieldsetLegend {
    padding: 0 13px;
    font-size: 0.875rem;
    color: #999;
  }

  .k-widget {
    max-height: 23rem;
  }
`;

const StyledInputTextWrapper = styled.div`
  label {
    align-self: center;
    margin-right: 10px;
    margin-bottom: 0;
    margin-left: 0;
    min-width: 5.2rem;
  }

  > div {
    display: flex;
    flex-direction: row;
    flex-grow: 1;
    align-self: center;
  }
`;

const StyledSelectParentKeywords = styled.div`
  .labelSelect {
    align-self: center;
    margin-right: 10px;
    margin-bottom: 0;
    min-width: 5.2rem;
  }

  .k-widget {
    max-height: 23rem;
    flex-grow: 1;
  }

  .dropdown-wrapper,
  .k-widget {
    width: 100%;
  }
`;
